import React, {useEffect} from 'react';
import {IValidationErrorObject} from '../../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../../incase-domain/components/button/Button';
import {
    IElementQuestionAnswer,
    IElementQuestionnaireWithAnswers,
    IQuestionAnswerType,
} from '../../../../../../incase-domain/elements/base/questionElementBase';
import {MAX_ANSWER_LENGTH} from '../../../../../../incase-domain/validators/elements/constant';
import {answerValidator} from '../../../../../../incase-domain/validators/elements/questionBase';
import {getCodeBookAnswerType, setCodebookAnswerType} from '../../../../../../utils/codebook.utils';
import {getErrorClasses} from '../../../../../../utils/validation';
import DebouncedTextInput from '../../../common/debounced/DebouncedTextInput';
import {AnswerTypes} from '../../../../../elements/question/question/QuestionAnswer';
import {getUpdatedAnswersForTypeChange} from '../../components/utils';

interface IChangeAnswerProps {
    answer: string;
    validationErrors: IValidationErrorObject | null;
    answerChangeHandler?: (index: number, value: string) => void;
    inactive?: boolean;
    index: number;
    highlightedElement: IElementQuestionnaireWithAnswers;
    highlightedElementChangeHandler: {single: (field: keyof IElementQuestionnaireWithAnswers | string, value: unknown) => void};
    codebookAnswer?: IElementQuestionAnswer;
}

function ChangeAnswer({
    answer,
    answerChangeHandler,
    index,
    validationErrors,
    inactive,
    highlightedElement,
    highlightedElementChangeHandler,
    codebookAnswer,
}: IChangeAnswerProps): JSX.Element {
    useEffect(() => {
        if (
            highlightedElement.singleCorrect &&
            highlightedElement.answers &&
            highlightedElement.answers.filter((answer) => answer.type === AnswerTypes.CORRECT).length > 1
        ) {
            const newAnswers = [...highlightedElement.answers].map((answer) => {
                if (answer.type === AnswerTypes.CORRECT) {
                    return {...answer, type: AnswerTypes.INCORRECT};
                }
                return answer;
            });
            highlightedElementChangeHandler.single('answers', newAnswers);
        }
    }, [highlightedElement.singleCorrect, highlightedElement.answers, highlightedElement.correctAnswers, highlightedElementChangeHandler]);

    function handleAnswerTypeChange(index: number): void {
        const nextTypeMapping: Record<string, IQuestionAnswerType> = {
            correct: AnswerTypes.DEBATABLE,
            incorrect: AnswerTypes.CORRECT,
            debatable: AnswerTypes.INCORRECT,
        };

        if (codebookAnswer) {
            setCodebookAnswerType(codebookAnswer, highlightedElement, highlightedElementChangeHandler, nextTypeMapping);
            return;
        }

        const newAnswers = getUpdatedAnswersForTypeChange(highlightedElement, index, nextTypeMapping);
        highlightedElementChangeHandler.single('answers', newAnswers);
    }

    function getAnswerType(): IQuestionAnswerType {
        return codebookAnswer
            ? getCodeBookAnswerType(highlightedElement, codebookAnswer.codebookEntryId)
            : highlightedElement.answers[index].type;
    }

    function renderAnswerType(): string {
        const answerType = getAnswerType();

        const icons = {
            correct: 'solar:check-circle-bold',
            incorrect: 'solar:close-circle-bold',
            debatable: 'solar:question-circle-bold',
        };

        return icons[answerType];
    }

    function colorAnswerType(): 'success' | 'error' | 'info' | 'primary' {
        const answerType = getAnswerType();

        switch (answerType) {
            case AnswerTypes.CORRECT:
                return 'success';
            case AnswerTypes.INCORRECT:
                return 'error';
            case AnswerTypes.DEBATABLE:
                return 'info';
            default:
                return 'primary';
        }
    }

    return (
        <div className={`l-group__item ${getErrorClasses(validationErrors, `answers.${index}`)}`} data-cy="change-answer">
            <DebouncedTextInput
                className="c-sidepanel__input"
                value={answer}
                onChange={(e) => {
                    if (answerChangeHandler && !inactive) {
                        answerChangeHandler(index, e);
                    }
                }}
                validator={(val) => {
                    const validation = answerValidator.shape.text.safeParse(val);
                    return validation.success;
                }}
                maxLength={MAX_ANSWER_LENGTH}
                key={index}
                disabled={!answerChangeHandler || !!codebookAnswer}
                trailing={
                    codebookAnswer && codebookAnswer.numberOfChildren ? undefined : (
                        <Button
                            size="xl"
                            color={colorAnswerType()}
                            leading={renderAnswerType()}
                            padded={false}
                            variant="ghost"
                            onClick={() => handleAnswerTypeChange(index)}
                        />
                    )
                }
            />
        </div>
    );
}

export default ChangeAnswer;
